import ja from "date-fns/locale/ja";
import React, { ReactNode, SyntheticEvent, useState } from "react";
import DatePicker, {
  registerLocale,
  CalendarContainer,
} from "react-datepicker";

import "react-datepicker/dist/react-datepicker.css";
import { Button } from "@/components/atoms/buttons/Button";
import IconCalendar from "public/icon/calendar.svg";

interface Props {
  id?: string;
  onChange: (date: Date | null, event?: SyntheticEvent<any, Event>) => void;
  handleClear?: (event?: SyntheticEvent<any, Event>) => void;
  selected?: Date | null;
  placeholder?: string;
  hasError?: boolean;
  selectsStart?: boolean;
  selectsEnd?: boolean;
  startDate?: Date | null;
  endDate?: Date | null;
  minDate?: Date | null;
  hideCalendarIcon?: boolean;
}

const DATE_REGEX = /^(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])$/;

export default function InputDate({
  id,
  onChange,
  handleClear,
  selected,
  placeholder = "入力なし",
  hasError,
  selectsStart,
  selectsEnd,
  startDate,
  endDate,
  minDate,
  hideCalendarIcon,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const border = hasError ? "border-accent" : "border-[#A1A6AC]";
  registerLocale(`ja`, ja);

  const submitHandle = () => {
    setIsOpen(false);
  };

  const changeHandle = (selectedDate: Date | null) => {
    onChange(selectedDate);
    setIsOpen(false);
  };

  const MyContainer: ({
    children,
  }: {
    children: ReactNode;
    className: string;
  }) => JSX.Element = ({ children, className }) => {
    return (
      <div className="p-4 bg-white rounded-lg border-0 react-datepicker-shadow">
        <CalendarContainer className={className}>
          <div className="relative">{children}</div>
          <div className="text-right">
            <div className="inline-block mt-2">
              <button
                type="button"
                onClick={(event) =>
                  handleClear ? handleClear(event) : onChange(null, event)
                }
                className="py-2 px-4 text-sm font-bold text-theme bg-theme-light/0 hover:bg-theme-thin focus:bg-theme-light rounded"
              >
                クリア
              </button>
            </div>
            <div className="inline-block mt-2 ml-4">
              <Button size="sm" onClick={submitHandle} label="決定" />
            </div>
          </div>
        </CalendarContainer>
      </div>
    );
  };

  function stringToDate(dateString: string) {
    const year = parseInt(dateString.substring(0, 4), 10);
    const month = parseInt(dateString.substring(4, 6), 10) - 1;
    const day = parseInt(dateString.substring(6, 8), 10);

    return new Date(year, month, day);
  }

  const handleBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    let date: string | Date = new Date();
    if (!value) return;
    if (value.includes("/")) return;

    if (DATE_REGEX.test(value)) {
      date = stringToDate(value);
    }
    changeHandle(date);
  };

  return (
    <div className="relative">
      <DatePicker
        onBlur={handleBlur}
        id={id}
        open={isOpen}
        onFocus={() => {
          setIsOpen(true);
        }}
        onClickOutside={(e) => setIsOpen(false)}
        onChange={changeHandle}
        dateFormat="yyyy/MM/dd"
        selected={selected}
        locale="ja"
        placeholderText={placeholder}
        className={`${border} w-full h-11`}
        calendarContainer={MyContainer}
        shouldCloseOnSelect={false}
        selectsStart={selectsStart}
        selectsEnd={selectsEnd}
        startDate={startDate}
        endDate={endDate}
        minDate={minDate}
        renderCustomHeader={({
          monthDate,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <div>
            <button
              type="button"
              aria-label="Previous Month"
              className={
                "react-datepicker__navigation react-datepicker__navigation--previous"
              }
              disabled={prevMonthButtonDisabled}
              onClick={decreaseMonth}
            >
              <span
                className={
                  "react-datepicker__navigation-icon react-datepicker__navigation-icon--previous"
                }
              >
                {"<"}
              </span>
            </button>
            <span className="react-datepicker__current-month">
              {monthDate.toLocaleString("ja", {
                month: "long",
                year: "numeric",
              })}
            </span>
            <button
              type="button"
              aria-label="Next Month"
              className={
                "static react-datepicker__navigation react-datepicker__navigation--next"
              }
              disabled={nextMonthButtonDisabled}
              onClick={increaseMonth}
            >
              <span
                className={
                  "react-datepicker__navigation-icon react-datepicker__navigation-icon--next"
                }
              >
                {">"}
              </span>
            </button>
          </div>
        )}
      ></DatePicker>
      {!hideCalendarIcon && (
        <IconCalendar className="block absolute top-1/2 right-0 mr-2 w-6 h-6 -translate-y-1/2 pointer-events-none fill-theme" />
      )}
    </div>
  );
}
